import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import { message } from 'ant-design-vue';
import { useLeads } from '@/composables/useLeads';
import type { LeadsListQuery, UpdateLeadRequest } from '@hyperke/shared';
import type { Lead, LeadDetailResponse } from '@/types';

/**
 * Leads store using Pinia
 * Manages leads state and actions with optimistic updates
 */
export const useLeadsStore = defineStore('leads', () => {
  const { fetchLeads, fetchLeadDetail, updateLead, addNote } = useLeads();

  // State
  const leads = ref<Lead[]>([]);
  const selectedLead = ref<LeadDetailResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const pagination = reactive({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  // Initialize filters - will be loaded from localStorage or use defaults
  const filters = reactive<Partial<LeadsListQuery>>({
    sort: 'last_interested_at:desc',
    // Note: has_transitioned_to is set per-view (Leads.vue sets 'Interested', Appointments.vue sets 'Appointment Booked')
    // It should not be stored in the default filters to avoid cross-contamination
    // Each view explicitly sets it on mount
  });

  /**
   * Normalize campaign_ids to the comma-joined string the API expects.
   * Accepts number[] (from the multi-select) or an existing string (from URL/store).
   * Returns undefined when empty so the filter is omitted entirely.
   */
  function normalizeCampaignIdsParam(value: unknown): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map((v) => String(v)).join(',') : undefined;
    }
    const s = String(value).trim();
    return s.length > 0 ? s : undefined;
  }

  /**
   * Normalize call_feedback to the comma-joined string the API expects.
   * Accepts string[] (from the multi-select) or an existing string (from URL/store).
   * Returns undefined when empty so the filter is omitted entirely.
   */
  function normalizeCallFeedbackParam(value: unknown): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) {
      const cleaned = value.map((v) => String(v).trim()).filter((v) => v.length > 0);
      return cleaned.length > 0 ? cleaned.join(',') : undefined;
    }
    const s = String(value).trim();
    return s.length > 0 ? s : undefined;
  }

  /**
   * Fetch leads list with filters and pagination
   * 
   * @param newFilters - Filter parameters
   * @returns Promise<void>
   */
  async function fetchLeadsList(newFilters?: Partial<LeadsListQuery>): Promise<void> {
    // DEBUG: Log incoming filters
    console.log('[LeadsStore] fetchLeadsList called with:', JSON.stringify(newFilters));

    // Ensure loading state is reset before starting new request
    // This prevents spinner from persisting if previous request had issues
    if (loading.value) {
      loading.value = false;
      // Small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    loading.value = true;
    try {
      const traceId = (window as any).__HYPERKE_TRACE_ID || 'unknown';
      
      // Get localStorage filters for logging (if they exist)
      let localStorageFilters = null;
      try {
        const { useAuthStore } = await import('@/stores/auth');
        const authStore = useAuthStore();
        const userId = authStore.user?.id || authStore.user?.email || 'anonymous';
        const key = `hyperke_filters_interested_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
          localStorageFilters = JSON.parse(stored);
        }
      } catch (error) {
        // Ignore errors
      }
      
      // Build query explicitly - don't merge, replace
      // This ensures undefined values properly clear filters
      const query: LeadsListQuery = {
        sort: newFilters?.sort || filters.sort || 'last_interested_at:desc',
        page: newFilters?.page || pagination.page,
        page_size: newFilters?.page_size || pagination.page_size,
      };
      
      // Explicitly set has_transitioned_to (view-specific)
      if (newFilters?.has_transitioned_to !== undefined) {
        query.has_transitioned_to = newFilters.has_transitioned_to;
      } else if (filters.has_transitioned_to !== undefined) {
        query.has_transitioned_to = filters.has_transitioned_to;
      }
      
      // FIX: Use 'in' operator to check if key was explicitly provided (even if undefined)
      // This allows clearing filters by passing key: undefined

      // has_transitioned_to (view-specific)
      if (newFilters && 'has_transitioned_to' in newFilters) {
        query.has_transitioned_to = newFilters.has_transitioned_to;
      } else if (filters.has_transitioned_to !== undefined) {
        query.has_transitioned_to = filters.has_transitioned_to;
      }

      // search - can be cleared by passing search: undefined
      if (newFilters && 'search' in newFilters) {
        query.search = newFilters.search;
      } else if (filters.search !== undefined) {
        query.search = filters.search;
      }

      // client_code
      if (newFilters && 'client_code' in newFilters) {
        query.client_code = newFilters.client_code;
      } else if (filters.client_code !== undefined) {
        query.client_code = filters.client_code;
      }

      // sdr_status
      if (newFilters && 'sdr_status' in newFilters) {
        query.sdr_status = newFilters.sdr_status;
      } else if (filters.sdr_status !== undefined) {
        query.sdr_status = filters.sdr_status;
      }

      // call_feedback (multi-select: stored/sent as comma-joined string)
      if (newFilters && 'call_feedback' in newFilters) {
        (query as any).call_feedback = normalizeCallFeedbackParam(newFilters.call_feedback);
      } else if ((filters as any).call_feedback !== undefined) {
        (query as any).call_feedback = normalizeCallFeedbackParam((filters as any).call_feedback);
      }

      // icp_classification (multi-select: comma-joined string, call_feedback pattern)
      const normalizeIcpParam = (value: unknown): string | undefined => {
        if (value === undefined || value === null) return undefined;
        const arr = Array.isArray(value) ? value : String(value).split(',');
        const joined = arr.map((v) => String(v).trim()).filter(Boolean).join(',');
        return joined || undefined;
      };
      if (newFilters && 'icp_classification' in newFilters) {
        (query as any).icp_classification = normalizeIcpParam(newFilters.icp_classification);
      } else if ((filters as any).icp_classification !== undefined) {
        (query as any).icp_classification = normalizeIcpParam((filters as any).icp_classification);
      }

      // icp_industry (single select)
      if (newFilters && 'icp_industry' in newFilters) {
        (query as any).icp_industry = newFilters.icp_industry;
      } else if ((filters as any).icp_industry !== undefined) {
        (query as any).icp_industry = (filters as any).icp_industry;
      }

      // source (lead ORIGIN: 'smartlead' | 'plusvibe', single select)
      if (newFilters && 'source' in newFilters) {
        (query as any).source = newFilters.source;
      } else if ((filters as any).source !== undefined) {
        (query as any).source = (filters as any).source;
      }

      // current_category_name - don't fall back to store filters to avoid stale filter issues
      if (newFilters && 'current_category_name' in newFilters) {
        query.current_category_name = newFilters.current_category_name;
      }

      // category_change_source - audit filter (e.g., AI_CATEGORIZATION)
      if (newFilters && 'category_change_source' in newFilters) {
        (query as any).category_change_source = newFilters.category_change_source;
      } else if ((filters as any).category_change_source !== undefined) {
        (query as any).category_change_source = (filters as any).category_change_source;
      }

      // assigned_inbox_manager_id
      if (newFilters && 'assigned_inbox_manager_id' in newFilters) {
        query.assigned_inbox_manager_id = newFilters.assigned_inbox_manager_id;
      } else if (filters.assigned_inbox_manager_id !== undefined) {
        query.assigned_inbox_manager_id = filters.assigned_inbox_manager_id;
      }

      // assigned_sdr_id
      if (newFilters && 'assigned_sdr_id' in newFilters) {
        query.assigned_sdr_id = newFilters.assigned_sdr_id;
      } else if (filters.assigned_sdr_id !== undefined) {
        query.assigned_sdr_id = filters.assigned_sdr_id;
      }

      // assigned_account_manager_id
      if (newFilters && 'assigned_account_manager_id' in newFilters) {
        query.assigned_account_manager_id = newFilters.assigned_account_manager_id;
      } else if (filters.assigned_account_manager_id !== undefined) {
        query.assigned_account_manager_id = filters.assigned_account_manager_id;
      }

      // date_from
      if (newFilters && 'date_from' in newFilters) {
        query.date_from = newFilters.date_from;
      } else if (filters.date_from !== undefined) {
        query.date_from = filters.date_from;
      }

      // date_to
      if (newFilters && 'date_to' in newFilters) {
        query.date_to = newFilters.date_to;
      } else if (filters.date_to !== undefined) {
        query.date_to = filters.date_to;
      }

      // meeting_date_from
      if (newFilters && 'meeting_date_from' in newFilters) {
        query.meeting_date_from = newFilters.meeting_date_from;
      } else if (filters.meeting_date_from !== undefined) {
        query.meeting_date_from = filters.meeting_date_from;
      }

      // meeting_date_to
      if (newFilters && 'meeting_date_to' in newFilters) {
        query.meeting_date_to = newFilters.meeting_date_to;
      } else if (filters.meeting_date_to !== undefined) {
        query.meeting_date_to = filters.meeting_date_to;
      }

      // campaign_ids (multi-select campaign filter)
      // Sent to the API as a comma-joined string ("123,456") — the form
      // leadsListQuerySchema accepts — to avoid axios array-param quirks.
      if (newFilters && 'campaign_ids' in newFilters) {
        (query as any).campaign_ids = normalizeCampaignIdsParam((newFilters as any).campaign_ids);
      } else if ((filters as any).campaign_ids !== undefined) {
        (query as any).campaign_ids = normalizeCampaignIdsParam((filters as any).campaign_ids);
      }

      const result = await fetchLeads(query);

      leads.value = result.data;
      pagination.page = result.pagination.page;
      pagination.page_size = result.pagination.page_size;
      pagination.total = result.pagination.total;
      pagination.total_pages = result.pagination.total_pages;

      // Update store filters to match query (single source of truth)
      // Remove filters that are undefined
      Object.keys(filters).forEach(key => {
        if ((query as any)[key] === undefined && key !== 'sort' && key !== 'has_transitioned_to') {
          delete (filters as any)[key];
        } else if ((query as any)[key] !== undefined) {
          (filters as any)[key] = (query as any)[key];
        }
      });

      // Add new filters from query
      Object.keys(query).forEach(key => {
        if (key !== 'page' && key !== 'page_size') {
          (filters as any)[key] = (query as any)[key];
        }
      });
    } catch (error) {
      // Error is handled by composable
      error.value = error instanceof Error ? error.message : 'Failed to fetch leads';
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch lead detail and open drawer
   * 
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @returns Promise<void>
   */
  async function openLeadDetail(leadId: number, campaignId: number): Promise<void> {
    loading.value = true;
    try {
      // Preserve assignments from leads list BEFORE fetching (more reliable than selectedLead)
      const leadInList = leads.value.find(
        (l) => l.lead_id === leadId && l.campaign_id === campaignId
      );
      // Also check selectedLead as fallback
      const selectedLeadAssignments = selectedLead.value?.lead?.lead_id === leadId && 
                                      selectedLead.value?.lead?.campaign_id === campaignId ? {
        assigned_sdr_id: selectedLead.value.lead.assigned_sdr_id,
        assigned_inbox_manager_id: selectedLead.value.lead.assigned_inbox_manager_id,
        assigned_account_manager_id: selectedLead.value.lead.assigned_account_manager_id,
      } : null;
      
      const preservedAssignments = leadInList ? {
        assigned_sdr_id: leadInList.assigned_sdr_id,
        assigned_inbox_manager_id: leadInList.assigned_inbox_manager_id,
        assigned_account_manager_id: leadInList.assigned_account_manager_id,
      } : selectedLeadAssignments;
      
      const detail = await fetchLeadDetail(leadId, campaignId);
      
      // Always preserve assignments when refreshing - don't lose them on refresh
      if (preservedAssignments) {
        detail.lead = { 
          ...detail.lead, 
          ...preservedAssignments,
        };
      }
      
      selectedLead.value = detail;
    } catch (error) {
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Close lead detail drawer
   */
  function closeLeadDetail(): void {
    selectedLead.value = null;
  }

  /**
   * Update lead with optimistic update
   * 
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param updates - Fields to update
   * @returns Promise<void>
   */
  async function updateLeadOptimistic(
    leadId: number,
    campaignId: number,
    updates: UpdateLeadRequest
  ): Promise<void> {
    // Find lead in current list
    const leadIndex = leads.value.findIndex(
      (l) => l.lead_id === leadId && l.campaign_id === campaignId
    );

      // Optimistic update
      if (leadIndex !== -1) {
        const originalLead = { ...leads.value[leadIndex] };
        // Preserve assignments before optimistic update
        const preservedAssignments = {
          assigned_sdr_id: leads.value[leadIndex].assigned_sdr_id,
          assigned_inbox_manager_id: leads.value[leadIndex].assigned_inbox_manager_id,
          assigned_account_manager_id: leads.value[leadIndex].assigned_account_manager_id,
        };
        leads.value[leadIndex] = { ...leads.value[leadIndex], ...updates, ...preservedAssignments };

      try {
        // Update on server
        const updatedLead = await updateLead(leadId, campaignId, updates);

        // Update with server response - ALWAYS preserve assignment fields from original lead
        // Assignments should never change when updating workflow fields, so always use original values
        const preservedAssignments = {
          assigned_sdr_id: originalLead.assigned_sdr_id,
          assigned_inbox_manager_id: originalLead.assigned_inbox_manager_id,
          assigned_account_manager_id: originalLead.assigned_account_manager_id,
        };
        
        // Always preserve assignments - workflow updates shouldn't change assignments
        leads.value[leadIndex] = { 
          ...updatedLead, 
          ...preservedAssignments,
        };
        
        // Note: Success toast is shown by the caller (e.g., Appointments.vue) to avoid duplicate messages
        // Do not show toast here - let the UI component handle user feedback

        // Update selected lead if it's the same - preserve assignments
        // Use originalLead assignments (most reliable) instead of selectedLead (might be stale)
        if (
          selectedLead.value &&
          selectedLead.value.lead.lead_id === leadId &&
          selectedLead.value.lead.campaign_id === campaignId
        ) {
          // Always preserve assignments from originalLead - workflow updates shouldn't change assignments
          selectedLead.value.lead = { 
            ...updatedLead, 
            assigned_sdr_id: originalLead.assigned_sdr_id,
            assigned_inbox_manager_id: originalLead.assigned_inbox_manager_id,
            assigned_account_manager_id: originalLead.assigned_account_manager_id,
          };
        }
      } catch (error: any) {
        // Rollback on error
        if (leadIndex !== -1) {
          leads.value[leadIndex] = originalLead;
        }
        
        // P0.3 Fix: Show detailed error messages with validation details
        const details = error.response?.data?.details;
        if (details && Array.isArray(details) && details.length > 0) {
          const validationError = details[0];
          const fieldName = validationError.path?.join('.') || 'field';
          const errorMsg = validationError.message || 'Validation failed';
          message.error(`${fieldName}: ${errorMsg}`);
        } else if (error.response?.data?.message) {
          message.error(error.response.data.message);
        } else {
          message.error('Failed to update lead. Please try again.');
        }
        throw error;
      }
    } else {
      // If lead not in list, just update on server
      await updateLead(leadId, campaignId, updates);
    }
  }

  /**
   * Add note to lead
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param noteText - Note text
   * @returns Promise<void>
   */
  async function addLeadNote(
    leadId: number,
    campaignId: number,
    noteText: string
  ): Promise<void> {
    try {
      await addNote(leadId, campaignId, noteText);

      // Refresh selected lead if it's the same
      if (
        selectedLead.value &&
        selectedLead.value.lead.lead_id === leadId &&
        selectedLead.value.lead.campaign_id === campaignId
      ) {
        await openLeadDetail(leadId, campaignId);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Insert a new lead immediately into the leads array without API call
   * Handles sorting, filtering, and pagination automatically
   *
   * @param newLead - Lead data from WebSocket notification
   */
  function insertLeadImmediately(newLead: Lead): void {
    console.log('[Leads Store] Inserting lead immediately:', newLead);

    // Check if lead already exists (dedupe)
    const exists = leads.value.some(
      lead => lead.lead_id === newLead.lead_id && lead.campaign_id === newLead.campaign_id
    );

    if (exists) {
      console.log('[Leads Store] Lead already exists, skipping insert');
      return;
    }

    // Check if lead matches current filters
    if (!doesLeadMatchFilters(newLead, filters)) {
      console.log('[Leads Store] Lead does not match current filters, not inserting');
      return;
    }

    // Insert lead at the beginning of array (newest first)
    leads.value.unshift(newLead);

    // Update pagination total
    pagination.total += 1;

    // Enforce page size limit (remove last if exceeds)
    if (leads.value.length > pagination.page_size) {
      leads.value = leads.value.slice(0, pagination.page_size);
    }

    console.log('[Leads Store] ✅ Lead inserted immediately');
  }

  /**
   * Check if a lead matches the current active filters
   */
  function doesLeadMatchFilters(lead: Lead, activeFilters: Partial<LeadsListQuery>): boolean {
    // If no filters, everything matches
    if (!activeFilters || Object.keys(activeFilters).length === 0) {
      return true;
    }

    // Check each filter condition
    if (activeFilters.client_code && lead.client_code !== activeFilters.client_code) {
      return false;
    }

    if (activeFilters.sdr_status && lead.sdr_status !== activeFilters.sdr_status) {
      return false;
    }

    if (activeFilters.campaign_id && lead.campaign_id !== Number(activeFilters.campaign_id)) {
      return false;
    }

    // Add more filter checks as needed based on your actual filter schema
    return true;
  }

  return {
    leads,
    selectedLead,
    loading,
    error,
    pagination,
    filters,
    fetchLeadsList,
    openLeadDetail,
    closeLeadDetail,
    updateLeadOptimistic,
    addLeadNote,
    insertLeadImmediately,
    doesLeadMatchFilters,
  };
});